import { baseAgencyParameter } from '@app/pcs/data/agency-parameter.fragment';
import { DictionaryEntity, LocoType, locoTypeDetailed } from '@app/pcs/models';
import { ParameterSetCode } from '@dossier/models/parameter-set-code';
import { TypeOfService } from '@dossier/models/type-of-service';
import { gql } from 'apollo-angular';

export interface BrakeType {
  id?: number;
  shortName?: string;
}

export interface TrainParameters {
  responsibleImAgencyId?: number;
  trainLength?: number;
  trainWeight?: number;
  brakeType?: BrakeType;
  brakePercentage?: number;
  maxAxleWeight?: number; // double
  freeText?: string;
  hasEmergBrakeBypass?: boolean;
  trafficType?: string;
  commercialTrafficType?: DictionaryEntity;
  typeOfService?: TypeOfService;

  trainMaxSpeed?: number;
  plannedSpeed?: number;
  highestPlannedSpeed?: number;
  coasting?: boolean;

  routeClass?: DictionaryEntity;
  pushPullTrain?: boolean;
  brakeWeight?: number;
  minBrakeWeightImPercentage?: string;
  lengthOfCarriages?: number;
  weightOfCarriages?: number;

  trainCCSystems?: DictionaryEntity[];
  trainRadioSystem?: DictionaryEntity;
  exceptionalGaugingIdents?: string[];
  locoIdents?: LocoIdent[];

  profileP1?: string;
  profileP2?: string;
  profileC1?: string;
  profileC2?: string;

  dangerousGoodsIndications?: DangerousGoodsIndication[];

  tiltingFunction?: DictionaryEntity;

  imParameters?: any[];

  mandatoryFields?: any;
  notSupportedFields?: any;
  parameterSetCodeTemplate?: ParameterSetCode;
  usedCatalogTrainParameters?: UsedCatalogTrainParameters;
}

export interface LocoIdent {
  id?: number;
  tractionRole?: DictionaryEntity;
  unitPositionInGroup?: DictionaryEntity;
  locoType?: LocoType;
  isEmpty?: boolean;
  hybridLocoType?: DictionaryEntity;
}

export interface DangerousGoodsIndication {
  id?: number;
  hazardNumber?: string;
  unMaterialNumber?: string;
  ridClassification?: string;
  weightOfDangerousGood?: number;
  dangerLabels?: DictionaryEntity[];
  dangerousGoodsVolume?: number;
  limitedQuantityIndicator?: boolean;
  packageGroup?: DictionaryEntity;
}

export enum TrainParametersPropertyMap {
  TrainWeight = 'trainLoad',
  BrakeType = 'brakeTypeId',
  MaxAxleWeight = 'hubLoad',
  ImParameters = 'trasseElementParameters',
  Comment = 'freeText',
  ExceptionalGaugingIdents = 'teExceptionalGaugingIdents',
  TrainRadioSystem = 'trainRadioSystemId',
  DangerousGoodsIndications = 'teDangerousGoodsIndications',
  PackageGroup = 'packageGroup',
  CommercialTrafficType = 'commercialTrafficTypeId'
}

export enum LocoIdentPropertyMap {
  LocoTypeNumber = 'id'
}

export interface UsedCatalogTrainParameters {
  parameterRecommendation: boolean;
  trainParameters: TrainParameters;
}

export const commonTrainParameters = gql`
  fragment commonTrainParameters on TrainParameters {
    responsibleImAgencyId
    trainLength
    trainWeight
    brakeType {
      id
      shortName
    }
    brakePercentage
    maxAxleWeight
    freeText
    hasEmergBrakeBypass
    trafficType
    typeOfService {
      specialServices {
        id
        description
      }
      facilityTypes {
        id
        description
      }
      characteristics {
        id
        description
      }
    }
    trainMaxSpeed
    plannedSpeed
    highestPlannedSpeed
    coasting
    routeClass {
      id
      description
    }
    pushPullTrain
    brakeWeight
    minBrakeWeightImPercentage
    lengthOfCarriages
    weightOfCarriages
    exceptionalGaugingIdents
    trainCCSystems {
      id
      description
    }
    trainRadioSystem {
      id
      description
    }
    locoIdents {
      id
      tractionRole {
        id
        description
      }
      unitPositionInGroup {
        id
        description
      }
      locoType {
        ...locoTypeDetailed
      }
      hybridLocoType {
        id
        description
      }
    }
    tiltingFunction {
      id
      description
    }
    profileP1
    profileP2
    profileC1
    profileC2
    dangerousGoodsIndications {
      id
      hazardNumber
      unMaterialNumber
      ridClassification
      weightOfDangerousGood
      dangerousGoodsVolume
      limitedQuantityIndicator
      packageGroup {
        id
        description
      }
      dangerLabels {
        id
        description
      }
    }
    mandatoryFields
    notSupportedFields
    usedCatalogTrainParameters {
      parameterRecommendation
      trainParameters {
        trainLength
        trainWeight
        lengthOfCarriages
        weightOfCarriages
      }
    }
    commercialTrafficType {
      id
      description
    }
  }
  ${locoTypeDetailed}
`;

export const childImParameter = gql`
  fragment childImParameter on ImParameter {
    id
    mandatory
    missingValue
    agencyParameter {
      ...baseAgencyParameter
    }
    value
    values
  }
  ${baseAgencyParameter}
`;

export const trainParametersDetailed = gql`
  fragment trainParametersDetailed on TrainParameters {
    ...commonTrainParameters
    imParameters {
      id
      agencyParameter {
        ...baseAgencyParameter
      }
      mandatory
      missingValue
      children {
        ...childImParameter
        children {
          ...childImParameter
          children {
            ...childImParameter
          }
        }
      }
      value
      values
    }
    parameterSetCodeTemplate {
      id
      name
    }
  }
  ${commonTrainParameters}
  ${baseAgencyParameter}
  ${childImParameter}
`;
